// Mitra does not implement Mastodon's follow `notify` setting. Watch each
// account's OPs locally, using the same polling lifecycle as thread watches.
export const USER_POST_WATCHER_STORAGE_KEY = "chan_fe_user_posts_v1";
const PAGE_SIZE = 40;
const HISTORY_LIMIT = 500;

function ownPosts(rows, accountId) {
  return rows.filter(post => post?.id && post.account?.id === accountId
    && !post.reblog && !post.in_reply_to_id && Number.isFinite(Date.parse(post.created_at)));
}

export function createUserPostWatcher({ storage, getScope, getSession, fetchPage, onChange = () => {}, now = Date.now }) {
  const snapshots = new Map();
  const storedValues = new Map();
  const pending = new Map();
  const keyFor = scope => `${USER_POST_WATCHER_STORAGE_KEY}:${scope}`;
  const empty = () => ({ watches: [], notifications: [] });

  function read(scope) {
    if (!scope) return empty();
    try {
      const raw = storage.getItem(keyFor(scope));
      if (snapshots.has(scope) && raw === storedValues.get(scope)) return snapshots.get(scope);
      const parsed = JSON.parse(raw || "null");
      const data = empty();
      data.watches = (Array.isArray(parsed?.watches) ? parsed.watches : [])
        .filter(w => w?.account?.id && typeof w.watchId === "string" && Number.isFinite(w.lastSeenAt))
        .map(w => ({ ...w, startedAt: Number.isFinite(w.startedAt) ? w.startedAt : w.lastSeenAt,
          seenIds: Array.isArray(w.seenIds) ? w.seenIds : [] }));
      data.notifications = (Array.isArray(parsed?.notifications) ? parsed.notifications : [])
        .filter(n => n?.id && n?.status?.id && n?.account?.id).slice(0, HISTORY_LIMIT);
      snapshots.set(scope, data);
      storedValues.set(scope, raw);
      return data;
    } catch {
      return snapshots.get(scope) || empty();
    }
  }

  function save(scope, data) {
    snapshots.set(scope, data);
    try {
      const raw = JSON.stringify(data);
      storage.setItem(keyFor(scope), raw);
      storedValues.set(scope, raw);
    } catch { /* Keep this tab's snapshot when storage is unavailable. */ }
    onChange();
  }

  const current = (scope, session) => scope === getScope() && session === getSession();
  const has = accountId => read(getScope()).watches.some(w => w.account.id === accountId);
  const notifications = () => read(getScope()).notifications;

  async function toggle(account) {
    const scope = getScope();
    const session = getSession();
    if (!scope || !account?.id) return;
    const data = read(scope);
    if (data.watches.some(w => w.account.id === account.id)) {
      save(scope, { ...data, watches: data.watches.filter(w => w.account.id !== account.id) });
      return;
    }
    const requestKey = `${scope}:${account.id}:toggle`;
    if (pending.has(requestKey)) return;
    const startedAt = now();
    const operation = (async () => {
      const page = await fetchPage(account.id, { limit: PAGE_SIZE });
      if (!Array.isArray(page.rows)) throw new Error("Invalid user posts response");
      if (!current(scope, session)) return;
      const latest = read(scope);
      if (latest.watches.some(w => w.account.id === account.id)) return;
      save(scope, { ...latest, watches: [...latest.watches, {
        account: { id: account.id, acct: account.acct, username: account.username, display_name: account.display_name },
        watchId: `${startedAt}:${Math.random()}`,
        startedAt,
        lastSeenAt: startedAt,
        seenIds: page.rows.filter(p => p?.id).map(p => p.id),
      }] });
    })();
    pending.set(requestKey, operation);
    try { await operation; } finally { pending.delete(requestKey); }
  }

  async function pollWatch(watch, scope, session) {
    const requestKey = `${scope}:${watch.account.id}:poll`;
    if (pending.has(requestKey)) return;
    const operation = (async () => {
      const posts = [];
      const seenIds = new Set(watch.seenIds);
      let firstPageIds;
      const cursors = new Set();
      let maxId;
      do {
        const page = await fetchPage(watch.account.id, { limit: PAGE_SIZE, maxId });
        if (!current(scope, session)) return;
        if (!Array.isArray(page.rows)) throw new Error("Invalid user posts response");
        firstPageIds ||= page.rows.filter(p => p?.id).map(p => p.id);
        const rows = ownPosts(page.rows, watch.account.id);
        for (const post of rows) {
          if (Date.parse(post.created_at) >= watch.startedAt && !seenIds.has(post.id)) {
            posts.push(post);
            seenIds.add(post.id);
          }
        }
        // Stop at a known server row, not a publication-time boundary: delayed
        // federation/backfills can arrive out of order. IDs stay opaque.
        if (page.rows.some(post => watch.seenIds.includes(post?.id)) || !page.rows.length) break;
        maxId = page.nextMaxId;
        if (maxId && cursors.has(maxId)) throw new Error("User posts cursor did not advance");
        cursors.add(maxId);
      } while (maxId);
      if (!current(scope, session) || (!posts.length
        && (!firstPageIds.length || JSON.stringify(firstPageIds) === JSON.stringify(watch.seenIds)))) return;
      const data = read(scope);
      const active = data.watches.find(w => w.account.id === watch.account.id);
      // Disabling/re-enabling or a newer poll in another tab invalidates this
      // result, so it cannot resurrect a watch or rewind its baseline.
      if (!active || active.watchId !== watch.watchId || active.lastSeenAt !== watch.lastSeenAt
        || JSON.stringify(active.seenIds) !== JSON.stringify(watch.seenIds)) return;
      const lastSeenAt = Math.max(watch.lastSeenAt, ...posts.map(p => Date.parse(p.created_at)));
      const updated = { ...active, lastSeenAt, seenIds: firstPageIds };
      const byId = new Map(data.notifications.map(n => [n.id, n]));
      for (const post of posts) {
        byId.set(post.id, { id: post.id, type: "status", account: post.account, status: post, created_at: post.created_at });
      }
      save(scope, {
        watches: data.watches.map(w => w.account.id === active.account.id ? updated : w),
        notifications: [...byId.values()].sort((a, b) => Date.parse(b.created_at) - Date.parse(a.created_at)).slice(0, HISTORY_LIMIT),
      });
    })();
    pending.set(requestKey, operation);
    try { await operation; } catch { /* Retry from the same baseline on the next poll. */ }
    finally { pending.delete(requestKey); }
  }

  async function poll() {
    const scope = getScope();
    const session = getSession();
    if (!scope) return;
    await Promise.all(read(scope).watches.map(watch => pollWatch(watch, scope, session)));
  }

  return { has, notifications, toggle, poll };
}
